/**
 * Shared Application Constants
 *
 * This file contains all shared constants used across the application.
 * All constants are consolidated here for easy maintenance and single source of truth.
 */

import {
  Psychology as PromptIcon,
  Analytics as AnalyticsIcon,
  Science as ExperimentIcon,
  Assessment as MonitoringIcon,
  Security as GovernanceIcon,
  Settings as SettingsIcon,
  PlayArrow as ExecutionIcon,
  Tune as ConfigIcon,
} from '@mui/icons-material';

import { FONT_PRESETS, DEFAULT_THEME_FORM_DATA, TABLE_CONFIG } from '../theme';

// ============================================================================
// PAGINATION & DATA RETRIEVAL CONSTANTS
// ============================================================================

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 25;

// Maximum page size for API requests (matches backend MAX_PAGE_SIZE)
export const MAX_PAGE_SIZE = 100;

// Maximum limit for analytics endpoints (templates analytics, search, etc.)
// This matches MAX_ANALYTICS_LIMIT from backend constants
export const MAX_ANALYTICS_LIMIT = 200;

// ============================================================================
// TIMING & DELAY CONSTANTS (in milliseconds)
// ============================================================================

// UI Delays
export const UI_DELAY_BACKGROUND = 100; // Small delay for background operations (non-blocking)
export const UI_DELAY_DEBOUNCE = 300; // Debounce delay for search/input
export const UI_DELAY_STABILIZATION = 500; // UI stabilization delay after loading
export const UI_DELAY_NAVIGATION = 300; // Short delay before navigation after actions (was 1500ms)

// Message Display Durations
export const MESSAGE_DURATION_SUCCESS = 2000; // Success message auto-hide duration
export const MESSAGE_DURATION_SNACKBAR = 3000; // Snackbar auto-hide duration

// Request Timeouts
export const REQUEST_TIMEOUT_DEFAULT = 8000; // Default request timeout (8 seconds)
export const REQUEST_TIMEOUT_SESSION_VERIFICATION = 8000; // Session verification timeout
export const REQUEST_TIMEOUT_LOADING_INDICATOR = 10000; // Loading indicator timeout (10 seconds)
export const REQUEST_TIMEOUT_LONG_OPERATION = 60000; // Long operation timeout (60 seconds) - for bulk operations
export const REQUEST_TIMEOUT_DEFAULT_API = 180000; // Default API request timeout (180 seconds / 3 minutes) - for most requests
export const REQUEST_TIMEOUT_LLM_EXECUTION = 300000; // LLM execution timeout (300 seconds / 5 minutes) - for template execute endpoints
export const REQUEST_TIMEOUT_ANALYTICS = 30000; // Analytics timeout (30 seconds) - matches API Gateway limit

// Cache TTL (Time To Live) - in milliseconds
export const CACHE_TTL_VERY_SHORT = 5000; // 5 seconds - for API request cache
export const CACHE_TTL_SHORT = 30000; // 30 seconds - for frequently changing data
export const CACHE_TTL_MEDIUM = 60000; // 1 minute - for moderately changing data
export const CACHE_TTL_SEARCH = 180000; // 3 minutes - for search results
export const CACHE_TTL_LONG = 300000; // 5 minutes - for stable data

// ============================================================================
// SEARCH & FILTER CONSTANTS
// ============================================================================

export const SEARCH_MIN_CHARS = 3; // Minimum characters for search query
export const SEARCH_DEBOUNCE_MS = 300; // Debounce delay for search input
export const SEARCH_CACHE_SIZE = 20; // Maximum number of search results to cache

// ============================================================================
// AUTHENTICATION CONSTANTS
// ============================================================================

export const AUTH_TOKEN_SKEW_SECONDS = 30; // Token validation time skew (seconds)

// LocalStorage key names (application constants - not configurable via env vars)
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'llamasee_auth_token',
  USER_DATA: 'llamasee_user_data',
  TENANT_DATA: 'llamasee_tenant_data',
};

// Default return path after authentication (application constant)
export const DEFAULT_RETURN_PATH = '/prompt-dashboard';

// Application version (reads from package.json, fallback to '0.0.1')
export const APP_VERSION = process.env.npm_package_version || '0.0.1';

// Default OAuth domain (application constant)
export const DEFAULT_OAUTH_DOMAIN = 'oauth.llamasee.com';

// Localhost port constants (application constants - not configurable via env vars)
export const DEFAULT_LOCALHOST_API_PORT = '8000';
export const DEFAULT_LOCALHOST_FRONTEND_PORT = '3000';

// ============================================================================
// CALCULATION CONSTANTS
// ============================================================================

export const PERCENTAGE_BASE = 100; // Base for percentage calculations
export const RATING_CONVERSION_FACTOR = 20; // Convert 0-5 rating to 0-100 scale

// ============================================================================
// PERFORMANCE SCORING CONSTANTS
// ============================================================================

// Performance scoring weights (must sum to 1.0)
export const PERFORMANCE_WEIGHT_RATING = 0.6; // 60% weight on rating
export const PERFORMANCE_WEIGHT_SUCCESS_RATE = 0.25; // 25% weight on success rate
export const PERFORMANCE_WEIGHT_FEEDBACK_COVERAGE = 0.1; // 10% weight on feedback coverage
export const PERFORMANCE_WEIGHT_USAGE = 0.05; // 5% weight on usage
export const PERFORMANCE_WEIGHT_SUCCESS_ONLY = 0.5; // 50% weight when no ratings available

// Feedback distribution thresholds (for good/bad feedback categorization)
export const FEEDBACK_DISTRIBUTION_HIGH_RATING = {
  good: 0.8, // 80% good feedback for high ratings (>= 4.0)
  bad: 0.1, // 10% bad feedback for high ratings
};
export const FEEDBACK_DISTRIBUTION_MEDIUM_RATING = {
  good: 0.5, // 50% good feedback for medium ratings (>= 3.0)
  bad: 0.2, // 20% bad feedback for medium ratings
};
export const FEEDBACK_DISTRIBUTION_LOW_RATING = {
  good: 0.2, // 20% good feedback for low ratings (< 3.0)
  bad: 0.5, // 50% bad feedback for low ratings
};

// Feedback threshold percentages
export const FEEDBACK_THRESHOLD_HIGH = 0.7; // 70% threshold for high feedback coverage
export const FEEDBACK_THRESHOLD_LOW = 0.1; // 10% threshold for low feedback coverage

// ============================================================================
// EXPERIMENT CONSTANTS
// ============================================================================

export const EXPERIMENT_MIN_SAMPLE_SIZE = 100; // Minimum sample size for experiments
export const EXPERIMENT_MIN_SAMPLE_SIZE_INPUT = 10; // Minimum input value for sample size
export const EXPERIMENT_MAX_SAMPLE_SIZE_INPUT = 10000; // Maximum input value for sample size
export const EXPERIMENT_DEFAULT_STATISTICAL_SIGNIFICANCE = 0.95; // Default statistical significance (95%)

// ============================================================================
// DECIMAL PRECISION CONSTANTS
// ============================================================================

export const DECIMAL_PRECISION_COST = 4; // Decimal places for cost values (e.g., 0.0001)
export const DECIMAL_PRECISION_PERCENTAGE = 1; // Decimal places for percentages (e.g., 95.5%)
export const DECIMAL_PRECISION_TIME = 2; // Decimal places for time values (e.g., 1.23s)
export const DECIMAL_PRECISION_RATING = 1; // Decimal places for ratings (e.g., 4.5)
export const DECIMAL_PRECISION_SCORE = 1; // Decimal places for scores (e.g., 85.5)

// ============================================================================
// TIME PERIOD CONSTANTS
// ============================================================================

export const TIME_PERIOD_OPTIONS = [
  { value: 'all-time', label: 'All Time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Convert time period string to number of days
 * @param {string} period - Time period string (e.g., '7d', '30d', 'all-time')
 * @returns {number|null} - Number of days or null for all-time
 */
export const getDaysFromPeriod = period => {
  if (period === 'all-time') {
    return null; // No days filter for all-time
  }
  if (period === 'custom') {
    return 30; // Fallback when custom is selected without explicit range
  }
  // Handle '7d', '30d', '90d' format
  const match = period.match(/^(\d+)d$/);
  if (match) {
    return parseInt(match[1]);
  }
  // Default fallback
  return 30;
};

// ============================================================================
// AI PROVIDER MODEL CONSTANTS
// ============================================================================

/**
 * AI Provider Model Names and Configuration
 * Updated with most current models as of 2025
 */
export const AI_PROVIDER_MODELS = {
  openai: {
    models: [
      'gpt-5',
      'gpt-4.1',
      'gpt-4o',
      'gpt-4-turbo',
      'gpt-4',
      'gpt-o3-mini',
      'o1-preview',
      'o1-mini',
      'gpt-3.5-turbo',
    ],
    modelConfig: {
      'gpt-5': { max_tokens: 200000 }, // Flagship model - strong reasoning, multimodal
      'gpt-4.1': { max_tokens: 128000 }, // Latest GPT-4 variant
      'gpt-4o': { max_tokens: 16384 },
      'gpt-4-turbo': { max_tokens: 128000 },
      'gpt-4': { max_tokens: 8192 },
      'gpt-o3-mini': { max_tokens: 128000 }, // Lightweight o-series variant
      'o1-preview': { max_tokens: 200000 },
      'o1-mini': { max_tokens: 128000 },
      'gpt-3.5-turbo': { max_tokens: 16384 },
    },
  },
  anthropic: {
    models: [
      // Claude 3.5 models (latest, verified working - require date suffix)
      'claude-3-5-sonnet-20241022', // Latest Sonnet model - verified working ✅
      'claude-3-5-haiku-20241022', // Latest Haiku model - verified working ✅
      // Claude 3 models (require date suffix)
      'claude-3-opus-20240229', // Opus model - verified working ✅
      'claude-3-sonnet-20240229', // Sonnet model - verified working ✅
      'claude-3-haiku-20240307', // Haiku model - verified working ✅
      // Claude 2 models
      'claude-2.1', // Claude 2.1 - verified working ✅
    ],
    modelConfig: {
      // Claude 3.5 models - verified working
      'claude-3-5-sonnet-20241022': { max_tokens: 8192 }, // Latest Sonnet - verified ✅
      'claude-3-5-haiku-20241022': { max_tokens: 8192 }, // Latest Haiku - verified ✅
      // Claude 3 models - verified working
      'claude-3-opus-20240229': { max_tokens: 4096 }, // Opus - verified ✅
      'claude-3-sonnet-20240229': { max_tokens: 4096 }, // Sonnet - verified ✅
      'claude-3-haiku-20240307': { max_tokens: 4096 }, // Haiku - verified ✅
      // Claude 2 models
      'claude-2.1': { max_tokens: 8192 }, // Claude 2.1 - verified ✅
    },
  },
  google: {
    models: [
      // Gemini 2.5 models (latest, verified working)
      'gemini-2.5-pro', // Latest Pro model - verified working ✅
      'gemini-2.5-flash', // Latest Flash model - verified working ✅
      'gemini-2.5-flash-lite', // Latest Flash Lite model - verified working ✅
      // Gemini 2.0 models (verified working)
      'gemini-2.0-flash', // Flash model - verified working ✅
      'gemini-2.0-flash-001', // Flash 001 variant - verified working ✅
      'gemini-2.0-flash-lite', // Flash Lite model - verified working ✅
      'gemini-2.0-flash-lite-001', // Flash Lite 001 variant - verified working ✅
    ],
    modelConfig: {
      // Gemini 2.5 models - verified working
      'gemini-2.5-pro': { max_tokens: 8192 }, // Latest Pro - verified ✅
      'gemini-2.5-flash': { max_tokens: 8192 }, // Latest Flash - verified ✅
      'gemini-2.5-flash-lite': { max_tokens: 8192 }, // Latest Flash Lite - verified ✅
      // Gemini 2.0 models - verified working
      'gemini-2.0-flash': { max_tokens: 8192 }, // Flash - verified ✅
      'gemini-2.0-flash-001': { max_tokens: 8192 }, // Flash 001 - verified ✅
      'gemini-2.0-flash-lite': { max_tokens: 8192 }, // Flash Lite - verified ✅
      'gemini-2.0-flash-lite-001': { max_tokens: 8192 }, // Flash Lite 001 - verified ✅
    },
  },
  private_llm: {
    models: ['qwen3:14b', 'llama3.2:3b', 'deepseek-r1:32b'],
    modelConfig: {
      'qwen3:14b': { max_tokens: 4096 },
      'llama3.2:3b': { max_tokens: 4096 },
      'deepseek-r1:32b': { max_tokens: 8000 },
    },
  },
};

/**
 * Normalize provider type to match AI_PROVIDER_MODELS keys
 * Handles various formats: 'OPENAI' -> 'openai', 'PRIVATE' -> 'private_llm', etc.
 *
 * @param {string} providerType - Provider type in any format
 * @returns {string} Normalized provider type matching AI_PROVIDER_MODELS keys
 */
export function normalizeProviderType(providerType) {
  if (!providerType || typeof providerType !== 'string') {
    return 'openai'; // Default fallback
  }

  const normalized = providerType.toLowerCase().trim();

  // Handle special cases
  if (normalized === 'private') {
    return 'private_llm';
  }

  // List of known provider types in AI_PROVIDER_MODELS
  const knownProviders = ['openai', 'anthropic', 'google', 'private_llm'];

  // Return normalized value if it's a known provider, otherwise return as-is
  if (knownProviders.includes(normalized)) {
    return normalized;
  }

  // For providers not in AI_PROVIDER_MODELS (azure, other), return normalized
  return normalized;
}

// ============================================================================
// NAVIGATION CONSTANTS
// ============================================================================

/**
 * Navigation items configuration based on CAO UX Specification
 * Each item defines the navigation structure for the application
 */
export const NAVIGATION_ITEMS = [
  {
    id: 'prompt-dashboard',
    label: 'Dashboard',
    icon: <AnalyticsIcon />,
    path: '/prompt-dashboard',
    description: 'Prompt Analytics, Performance Metrics, Cost Tracking',
  },
  {
    id: 'template-management',
    label: 'Template Management',
    icon: <PromptIcon />,
    path: '/templates',
    description: 'Template Management, Version Control, Execution Testing',
  },
  {
    id: 'executions',
    label: 'Executions',
    icon: <ExecutionIcon />,
    path: '/executions',
    description: 'Execution Management, Results Analysis, Experiment Tracking',
    children: [
      { id: 'execution-list', label: 'Execution List', path: '/executions' },
      {
        id: 'experiments',
        label: 'Experiments',
        path: '/executions/experiments',
      },
    ],
  },
  {
    id: 'experiments',
    label: 'Experiments',
    icon: <ExperimentIcon />,
    path: '/experiments',
    description: 'A/B Testing, Treatment Management, Results Analysis',
    children: [
      {
        id: 'experiment-list',
        label: 'Experiment List',
        path: '/experiments',
      },
      {
        id: 'create-experiment',
        label: 'Create New Experiment',
        path: '/experiments/create',
      },
      {
        id: 'experiment-results',
        label: 'Experiment Results',
        path: '/experiments/results',
      },
    ],
  },
  {
    id: 'analytics-insights',
    label: 'Analytics & Insights',
    icon: <MonitoringIcon />,
    path: '/analytics-insights',
    description:
      'Performance Metrics, Usage Analytics, Cost Tracking, AI Insights',
    requiresFeature: 'ANALYTICS_INSIGHTS', // Feature flag required to show this navigation item
    children: [
      {
        id: 'analytics',
        label: 'Analytics',
        path: '/analytics-insights/analytics',
      },
      {
        id: 'insights',
        label: 'Insights',
        path: '/analytics-insights/insights',
      },
    ],
  },
  {
    id: 'governance',
    label: 'Governance & Compliance',
    icon: <GovernanceIcon />,
    path: '/governance/dashboard',
    description: 'Content Check Analytics, Audit Trails, Risk Assessment',
    requiresFeature: 'GOVERNANCE_COMPLIANCE', // Feature flag required to show this navigation item
    children: [
      {
        id: 'governance-dashboard',
        label: 'Analytics Dashboard',
        path: '/governance/dashboard',
      },
      {
        id: 'audit-logs',
        label: 'Audit Logs',
        path: '/governance/audit-logs',
      },
      {
        id: 'supported-policies',
        label: 'Supported Policies',
        path: '/governance/policies',
      },
      {
        id: 'entity-information',
        label: 'Entity Information',
        path: '/governance/entities',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    path: '/settings',
    description: 'User Management, Theme Customization, API Configuration',
    requiresAdminOrOwner: true, // Only show to admin/owner users
    children: [
      {
        id: 'settings-groups',
        label: 'Groups Management',
        path: '/settings/groups', // Groups Management route nested under Settings
      },
      {
        id: 'settings-users',
        label: 'Users Management',
        path: '/settings/users', // Users Management route nested under Settings
      },
      {
        id: 'settings-theme-logo',
        label: 'Theme & Logo',
        path: '/settings/theme-logo', // Theme & Logo route nested under Settings
      },
      {
        id: 'settings-configuration',
        label: 'Configuration Management',
        path: '/settings/configuration', // Configuration Management route nested under Settings
        icon: <ConfigIcon />,
      },
    ],
  },
];

/**
 * Navigation styling constants
 */
export const NAVIGATION_STYLES = {
  drawer: {
    expanded: {
      width: 280,
      backgroundColor: '#f8fafc',
      borderRight: '1px solid #e2e8f0',
    },
    collapsed: {
      width: 64,
      backgroundColor: '#f8fafc',
      borderRight: '1px solid #e2e8f0',
      overflow: 'hidden',
    },
  },
  colors: {
    primary: '#2563eb',
    selectedBackground: '#dbeafe',
    selectedHover: '#bfdbfe',
    subItemSelectedBackground: '#f1f5f9',
    subItemSelectedHover: '#e2e8f0',
    textSecondary: '#64748b',
  },
  logo: {
    expanded: {
      height: 72,
      width: 'auto',
    },
    collapsed: {
      height: 24,
      width: 'auto',
    },
  },
};

// ============================================================================
// API BASE URL CONSTANTS BY ENVIRONMENT
// ============================================================================
// These URLs are used based on REACT_APP_ENVIRONMENT value
// NO hardcoded fallbacks - environment must be explicitly set

export const API_BASE_URLS = {
  localhost: 'http://localhost:8000', // For localhost environment
  development: 'https://dev-api.llamasee.com', // For development environment
  staging: 'https://staging-api.llamasee.com', // For staging environment
  production: 'https://api.llamasee.com', // For production environment
};

// ============================================================================
// TEMPLATE EDITOR CONSTANTS
// ============================================================================

// API Configuration
// NOTE: Do NOT use hardcoded fallbacks here
// Use the validated functions from config/api.js instead:
// - getApiBaseUrl() for base URL
// This prevents accidentally using wrong environment values
export const API_CONFIG = {
  // These are deprecated - use functions from config/api.js instead
  // BASE_URL and KEY should be retrieved via validated functions
  get BASE_URL() {
    throw new Error(
      'API_CONFIG.BASE_URL is deprecated. Use getApiBaseUrl() from config/api.js'
    );
  },
  get KEY() {
    throw new Error('API_CONFIG.KEY is deprecated.');
  },
};

// Master Template ID for AI-powered template generation
// This template should be designed to generate other templates based on user requirements
export const MASTER_TEMPLATE_ID = '6530582a-725f-497d-8814-2454f4b2d0b4';

// Template categories - using the actual enum values from the backend
export const TEMPLATE_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'insight_generation', label: 'Insight Generation' },
  { value: 'insight_summary', label: 'Insight Summary' },
  { value: 'comparison_analysis', label: 'Comparison Analysis' },
  { value: 'trend_analysis', label: 'Trend Analysis' },
  { value: 'anomaly_detection', label: 'Anomaly Detection' },
  { value: 'recommendation_generation', label: 'Recommendation Generation' },
  { value: 'data_visualization', label: 'Data Visualization' },
  { value: 'report_generation', label: 'Report Generation' },
  { value: 'custom', label: 'Custom' },
];

// Output formats - using the actual enum values from the backend
export const OUTPUT_FORMATS = [
  {
    value: 'text',
    label: 'Text Response',
    description: 'Plain text response from LLM',
  },
  {
    value: 'json',
    label: 'Structured JSON',
    description: 'Parsed and structured output',
  },
  {
    value: 'json_with_raw',
    label: 'JSON with Raw',
    description: 'Both JSON and raw text',
  },
  {
    value: 'custom',
    label: 'Custom Format',
    description: 'Custom parser format',
  },
];

// Template status options - using the actual enum values from the backend
export const TEMPLATE_STATUSES = [
  {
    value: 'draft',
    label: 'Draft',
    description: 'Work in progress, not ready for production',
    color: 'warning',
  },
  {
    value: 'active',
    label: 'Active',
    description: 'Ready for production use',
    color: 'success',
  },
  {
    value: 'deprecated',
    label: 'Deprecated',
    description: 'No longer recommended for use',
    color: 'error',
  },
  {
    value: 'archived',
    label: 'Archived',
    description: 'Removed from service, historical record only',
    color: 'default',
  },
];

// Note: Models are now loaded dynamically from AWS Secrets Manager
// No hardcoded defaults - all models come from the single source of truth (AWS)
export const DEFAULT_MODELS = {};

// Default form data structure
// Note: provider and model_name will be populated from AWS-provided adapters
export const DEFAULT_FORM_DATA = {
  name: '',
  description: '',
  template: '',
  category: 'general',
  tags: [],
  output_format: 'text',
  status: 'draft', // Default status for new templates
  model_config: {
    provider: '', // Will be set from first available AWS provider
    model_name: '', // Will be set from first available AWS model
    temperature: 0.7,
    max_tokens: 1000,
    top_p: 1.0,
    frequency_penalty: 0.0,
    presence_penalty: 0.0,
  },
};

// Model configuration field constraints
export const MODEL_CONFIG_CONSTRAINTS = {
  temperature: { min: 0, max: 2, step: 0.1 },
  max_tokens: { min: 1, max: 4000 },
  top_p: { min: 0, max: 1, step: 0.1 },
  frequency_penalty: { min: -2, max: 2, step: 0.1 },
  presence_penalty: { min: -2, max: 2, step: 0.1 },
};

// Helper text for model configuration fields
export const MODEL_CONFIG_HELPERS = {
  temperature: 'Controls randomness (0 = focused, 2 = creative)',
  max_tokens: 'Maximum response length',
  top_p: 'Controls response diversity',
  frequency_penalty: 'Reduces repetition',
  presence_penalty: 'Encourages new topics',
};

// ============================================================================
// THEME MANAGEMENT CONSTANTS
// ============================================================================

// Export font family options (from theme presets)
export const FONT_FAMILY_OPTIONS = FONT_PRESETS.map(preset => preset.value);

// Export default theme form data (from theme tokens)
export { DEFAULT_THEME_FORM_DATA };

// Export table configuration
export const TABLE_ROWS_PER_PAGE_OPTIONS = TABLE_CONFIG.ROWS_PER_PAGE_OPTIONS;

// Color fields for theme form (UI-specific)
export const COLOR_FIELDS = [
  { name: 'primary_color', label: 'Primary Color' },
  { name: 'secondary_color', label: 'Secondary Color' },
  { name: 'accent_color', label: 'Accent Color' },
  { name: 'background_color', label: 'Background Color' },
  { name: 'surface_color', label: 'Surface Color' },
  { name: 'text_color', label: 'Text Color' },
];
